import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Building2, Users, ClipboardList, Globe,
  ShieldCheck, LogOut, RefreshCcw, AlertTriangle, CheckCircle2,
  XCircle, HelpCircle, Trash2, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_META = {
  ACTIVE:      { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  SOLD:        { color: "bg-rose-100 text-rose-700",       icon: XCircle },
  OFF_MARKET:  { color: "bg-rose-100 text-rose-700",       icon: XCircle },
  NOT_FOUND:   { color: "bg-rose-100 text-rose-700",       icon: XCircle },
  BOT_BLOCKED: { color: "bg-amber-100 text-amber-800",     icon: AlertTriangle },
  UNKNOWN:     { color: "bg-slate-100 text-slate-700",     icon: HelpCircle },
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AdminAudits() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [audits, setAudits] = useState([]);
  const [loadingAudits, setLoadingAudits] = useState(false);
  const [running, setRunning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [autoRemove, setAutoRemove] = useState(false);
  const [expandedAuditId, setExpandedAuditId] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(r.data);
    } catch {
      navigate("/admin/login");
    } finally {
      setLoadingUser(false);
    }
  }, [navigate]);

  const fetchAudits = useCallback(async () => {
    setLoadingAudits(true);
    try {
      const r = await axios.get(`${API}/admin/inventory-audits?limit=20`, { withCredentials: true });
      setAudits(r.data || []);
    } catch (err) {
      logger.error("fetch audits failed", err);
      toast.error("Failed to load audit history");
    } finally {
      setLoadingAudits(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { if (user) fetchAudits(); }, [user, fetchAudits]);

  const runAudit = async () => {
    setRunning(true);
    setDialogOpen(false);
    const action = autoRemove ? "Running audit & auto-removing flagged units…" : "Running audit (dry-run)…";
    toast.loading(action, { id: "audit-toast" });
    try {
      const r = await axios.post(
        `${API}/admin/inventory-health-check`,
        { dry_run: !autoRemove, auto_remove: autoRemove },
        { withCredentials: true, timeout: 180000 },
      );
      toast.success(
        `Audit complete. Checked ${r.data.total_checked}, flagged ${r.data.flagged?.length ?? 0}, removed ${r.data.removed_property_ids?.length ?? 0}.`,
        { id: "audit-toast" },
      );
      setAutoRemove(false);
      await fetchAudits();
    } catch (err) {
      logger.error("audit failed", err);
      toast.error("Audit failed. Check backend logs.", { id: "audit-toast" });
    } finally {
      setRunning(false);
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {/* ignore */}
    navigate("/admin/login");
  };

  if (loadingUser) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="admin-audits-page">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1A1A2E] text-white flex flex-col">
        <div className="p-6 border-b border-slate-700">
          <Link to="/" className="font-bold text-xl">
            1031<span className="text-[#60A5FA]">Condo</span>
          </Link>
          <p className="text-xs text-slate-400 mt-1">Admin Dashboard</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {[
            { id: "dashboard",  icon: LayoutDashboard, label: "Dashboard",       to: "/admin" },
            { id: "properties", icon: Building2,       label: "Properties",      to: "/admin/properties" },
            { id: "scraper",    icon: Globe,           label: "Listing Importer", to: "/admin/scraper" },
            { id: "reports",    icon: ClipboardList,   label: "Client Reports",  to: "/admin/reports" },
            { id: "audits",     icon: ShieldCheck,     label: "Inventory Audit", to: "/admin/audits", active: true },
            { id: "leads",      icon: Users,           label: "Leads",           to: "/admin" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.to)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors ${
                item.active
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-700">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full text-slate-400 hover:text-white justify-start"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 overflow-auto">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1A1A2E]">Inventory Audit</h1>
              <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Verifies that every property's source URL still points to an active for-sale listing.
                Flags units marked SOLD, OFF_MARKET, or NOT_FOUND. <strong>BOT_BLOCKED</strong> verdicts
                (StreetEasy CAPTCHA) are never auto-removed and need manual review.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={fetchAudits}
                disabled={loadingAudits}
                data-testid="audits-refresh-btn"
              >
                <RefreshCcw className={`w-4 h-4 mr-2 ${loadingAudits ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                onClick={() => setDialogOpen(true)}
                disabled={running}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                data-testid="audits-run-btn"
              >
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                {running ? "Running…" : "Run Audit"}
              </Button>
            </div>
          </div>

          {/* Audit list */}
          <div className="bg-white border border-slate-100 shadow-soft" data-testid="audits-list">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-xl font-medium text-[#1A1A2E]">Recent Audits</h2>
              <span className="text-xs text-slate-500">{audits.length} run{audits.length === 1 ? "" : "s"}</span>
            </div>

            {audits.length === 0 && !loadingAudits ? (
              <div className="p-12 text-center text-slate-500" data-testid="audits-empty">
                <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium text-slate-700">No audits yet</p>
                <p className="text-sm mt-1">Click "Run Audit" to verify all property listings.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {audits.map((a) => {
                  const flaggedCount = a.flagged?.length ?? 0;
                  const removedCount = a.removed_property_ids?.length ?? 0;
                  const isOpen = expandedAuditId === a.audit_id;
                  return (
                    <div key={a.audit_id} data-testid={`audit-row-${a.audit_id}`}>
                      <button
                        onClick={() => setExpandedAuditId(isOpen ? null : a.audit_id)}
                        className="w-full p-5 grid grid-cols-12 items-center gap-4 hover:bg-slate-50 text-left"
                      >
                        <div className="col-span-3">
                          <p className="text-sm font-medium text-[#1A1A2E]">{formatDateTime(a.started_at)}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{a.audit_id}</p>
                        </div>
                        <div className="col-span-2 text-sm">
                          <span className="text-slate-500">Checked </span>
                          <span className="font-medium text-[#1A1A2E]">{a.total_checked}</span>
                        </div>
                        <div className="col-span-5 flex flex-wrap gap-2">
                          {Object.entries(a.counts || {}).map(([verdict, n]) => {
                            const meta = VERDICT_META[verdict] || VERDICT_META.UNKNOWN;
                            const Icon = meta.icon;
                            return (
                              <span
                                key={verdict}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}
                              >
                                <Icon className="w-3 h-3" />
                                {verdict}: {n}
                              </span>
                            );
                          })}
                        </div>
                        <div className="col-span-2 text-right text-xs">
                          {a.auto_remove ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-100 text-rose-700 font-medium">
                              <Trash2 className="w-3 h-3" />
                              Removed {removedCount}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                              Dry-run · {flaggedCount} flagged
                            </span>
                          )}
                        </div>
                      </button>

                      {isOpen && (
                        <div className="px-6 pb-6 -mt-1 bg-slate-50/60" data-testid={`audit-details-${a.audit_id}`}>
                          {flaggedCount === 0 ? (
                            <p className="text-sm text-slate-500 py-3">No flagged units in this audit.</p>
                          ) : (
                            <div className="overflow-hidden border border-slate-200 rounded">
                              <table className="w-full text-sm">
                                <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wide">
                                  <tr>
                                    <th className="text-left p-3">Building</th>
                                    <th className="text-left p-3">Property ID</th>
                                    <th className="text-left p-3">Verdict</th>
                                    <th className="text-left p-3">Source URL</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {a.flagged.map((f) => {
                                    const meta = VERDICT_META[f.verdict] || VERDICT_META.UNKNOWN;
                                    return (
                                      <tr key={f.property_id} className="border-t border-slate-100">
                                        <td className="p-3 text-slate-700">{f.building_name || "—"}</td>
                                        <td className="p-3 font-mono text-xs text-slate-600">{f.property_id}</td>
                                        <td className="p-3">
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>
                                            {f.verdict}
                                          </span>
                                        </td>
                                        <td className="p-3">
                                          <a
                                            href={f.source_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[#0066FF] hover:underline truncate inline-block max-w-[420px]"
                                          >
                                            {f.source_url}
                                          </a>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </main>

      {/* Run Audit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="audit-run-dialog">
          <DialogHeader>
            <DialogTitle>Run Inventory Audit</DialogTitle>
            <DialogDescription>
              Crawls every property's source URL and classifies it as ACTIVE, SOLD, OFF_MARKET,
              NOT_FOUND, BOT_BLOCKED, or UNKNOWN. May take 30–120 seconds.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 p-4 border border-slate-200 rounded">
              <div>
                <Label htmlFor="auto-remove-switch" className="font-medium text-[#1A1A2E]">
                  Auto-remove flagged units
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  When enabled, units classified as <strong>NOT_FOUND</strong>, <strong>SOLD</strong>,
                  or <strong>OFF_MARKET</strong> will be permanently deleted from the database.
                  BOT_BLOCKED and UNKNOWN are never auto-removed.
                </p>
              </div>
              <Switch
                id="auto-remove-switch"
                checked={autoRemove}
                onCheckedChange={setAutoRemove}
                data-testid="audit-auto-remove-switch"
              />
            </div>

            {autoRemove && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>This will permanently delete flagged properties. This action cannot be undone.</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="audit-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={runAudit}
              className={autoRemove ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-[#0066FF] hover:bg-[#0052CC] text-white"}
              data-testid="audit-confirm-btn"
            >
              {autoRemove ? "Run & Delete Flagged" : "Run Dry-Run Audit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
